<template>
	<v-row>
		<v-col>
			<v-row v-if="!hideSearch">
				<v-col cols="auto">
					<!-- Search Table -->
					<!-- Filters rows except those headers marked `filterable: false` -->
					<v-text-field
							v-model="search"
							append-icon="mdi-magnify"
							class="rounded-lg"
							:dense="$vuetify.breakpoint.smAndDown"
							hide-details
							label="Search"
							outlined
							style="max-width: 320px;"
					></v-text-field>
				</v-col>
				<v-col
						v-if="!hideRowSelect && !!showDateFilter"
						class="align-center d-flex"
						cols="auto"
				>

				</v-col>
				<v-col v-if="showColumnSelect" cols="auto">
					<!-- Add/Remove Columns - optional -->
					<v-select
							v-model="activeHeaders"
							class="rounded-lg"
							:dense="$vuetify.breakpoint.smAndDown"
							hide-details
							item-value="text"
							:items="headers"
							label="Columns"
							:menu-props="{ maxHeight: '400' }"
							multiple
							outlined
							return-object
							style="max-width: 320px;"
							@change="
								activeHeaders = headers.filter(header => activeHeaders.includes(header))
							"
					>
						<!-- TODO: configure the number of selected columns shown with a computed -->
						<!-- This is solely for the select box display -->
						<template v-slot:selection="{ index, item }">
              <span v-if="index < maxShowSelectedColumns">
                {{
									`${item.text}${
											activeHeaders.length > index && index < maxShowSelectedColumns - 1
													? ',&nbsp;'
													: ''
									}`
								}}
              </span>
							<span
									v-else-if="index === maxShowSelectedColumns"
									class="font-weight-bold ms-2 px-2 rounded-xl secondary white--text"
									:class="{
										'text-caption2': $vuetify.breakpoint.xs,
										'text-body-2': $vuetify.breakpoint.sm
									}"
							>
                +{{ activeHeaders.length - maxShowSelectedColumns }}
              </span>
						</template>
						<template v-slot:item="{ active, attrs, item, on }">
							<v-list-item
									#default="{ active }"
									v-bind="attrs"
									:color="item.mandatory ? 'grey' : null"
									:disabled="item.mandatory"
									:ripple="false"
									v-on="on"
							>
								<v-list-item-action>
									<v-checkbox
											v-if="item.mandatory"
											color="grey lighten-2"
											dense
											:input-value="active"
											on-icon="mdi-lock"
											:ripple="false"
											small
									></v-checkbox>
									<v-checkbox
											v-else
											dense
											:input-value="active"
											:ripple="false"
											small
									></v-checkbox>
								</v-list-item-action>
								<v-list-item-content>
									<v-list-item-title
											v-if="item.mandatory"
											class="font-italic grey--text text--lighten-2"
									>
										{{ item.text }}
									</v-list-item-title>
									<v-list-item-title v-else>
										{{ item.text }}
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="!!showDateFilter" cols="auto">
					<v-dialog
							ref="dateFilterDialog"
							v-model="dateModal"
							persistent
							:return-value.sync="filterDateRange"
							width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
									v-model="filterDateRange"
									v-bind="attrs"
									append-icon="mdi-calendar"
									class="rounded-lg"
									:dense="$vuetify.breakpoint.smAndDown"
									hide-details
									label="Date Range"
									outlined
									readonly
									style="max-width: 320px"
									v-on="on"
							></v-text-field>
						</template>
						<v-date-picker
								v-model="filterDateRange"
								no-title
								range
								@input="filterItemsByDate"
						>
							<v-spacer></v-spacer>
							<v-btn color="primary" text @click="dateModal = false">Cancel</v-btn>
							<v-btn
									color="primary"
									text
									@click="$refs.dateFilterDialog.save(filterDateRange)"
							>
								OK
							</v-btn>
						</v-date-picker>
					</v-dialog>
				</v-col>


				<v-col v-if="hasHeaderFilters" cols="auto">
					<bw-filters
							:display-filters="displayFilters"
							:emit-on-change="true"
							:filters="receivedFilters"
							:show-buttons="false"
							@filters-changed="updateFilteredItems"
					></bw-filters>
				</v-col>
			</v-row>

			<v-row v-if="!hideSearch && !hideResetRow">
				<v-col cols="auto">
					<!-- Reset Filters -->
					<v-switch
							v-if="!hideRowSelect"
							v-model="rowSelect"
							:color="rowSelect ? 'grey darken-2' : 'grey lighten-1'"
							dense
							hide-details="auto"
							inset
							label="Row Select"
							@change="() => (selectedRows = rowSelect ? selectedRows : [])"
					></v-switch>
				</v-col>
				<v-spacer v-if="$vuetify.breakpoint.mdAndUp" class="align-self-center">
					<v-divider class="opacity-70"></v-divider>
				</v-spacer>
				<v-col class="align-center d-flex" cols="auto">
					<!-- Reset Filters -->
					<v-btn
							v-if="!hideReset"
							class="rounded-lg"
							color="white"
							dark
							depressed
							:small="$vuetify.breakpoint.smAndDown"
							@click="resetTable"
							v-ripple="{ class: 'error--text' }"
					>
						<v-icon
								v-if="$vuetify.breakpoint.mdAndUp || $vuetify.breakpoint.xsOnly"
								:class="{
                'me-2': $vuetify.breakpoint.smAndUp,
                'ms-n1': $vuetify.breakpoint.smAndUp
              }"
								color="error"
						>
							mdi-history
						</v-icon>
						<span v-if="$vuetify.breakpoint.smAndUp" class="error--text">Reset</span>
					</v-btn>
					<!--  More Actions -->
					<template v-if="rowSelect && selectedRows.length">
						<v-col class="align-center d-flex" cols="auto">
							<slot name="moreActions" :selectedRows="selectedRows"></slot>
						</v-col>
					</template>
				</v-col>
			</v-row>
			<!--  Action Area -->
			<v-row v-if="this.$slots.actionArea">
				<v-col cols="auto">
				</v-col>
				<v-spacer v-if="$vuetify.breakpoint.mdAndUp" class="align-self-center">
					<v-divider class="opacity-70"></v-divider>
				</v-spacer>
				<template>
					<v-col class="align-center d-flex" cols="auto">
						<slot name="actionArea"></slot>
					</v-col>
				</template>
			</v-row>

			<v-row>
				<v-col>
					<ui-box fluid noInnerBackground noPadding>
						<v-row no-gutters>
							<v-col
									:style="{
										marginBottom: 'hide-default-footer' in $attrs ? '-1px' : '',
										marginTop: 'hide-default-header' in $attrs ? '-1px' : ''
									}"
							>
								<template v-if="this.$slots.alternativeDataTable">
									<slot name="alternativeDataTable"></slot>
								</template>
								<v-data-table
										v-else
										v-model="selectedRows"
										v-bind="{ ...$attrs, ...localAttrs }"
										class="bw-table"
										:class="{ 'no-border': noBorder }"
										:custom-filter="customSearch"
										:disable-sort="disableSort"
										:headers="activeHeaders"
										:items="filteredItems"
										:search="search"
										:show-select="rowSelect"
										:sort-by="sortBy"
										:sort-desc="sortDesc"
										v-on="$listeners"
								>
									<!-- Header slots - don't overwrite!!! -->
									<template v-for="(_, i) in filters" v-slot:[`header.${i}`]="{ header }">
                    <span :key="`header.${i}-text`">
                      {{ header.text }}
                      <div :key="`header.${i}-filter-icon`" class="filter-menu-btn">
                        <v-menu
														:close-on-content-click="false"
														fixed
														left
														:nudge-width="200"
														offset-y
														style="position: absolute; right: 0"
														transition="slide-y-transition"
												>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" icon v-on="on">
                              <v-icon
																	:color="
                                  activeFilters[header.value] &&
                                  activeFilters[header.value].length <
                                    filters[header.value].length
                                    ? 'red'
                                    : 'default'
                                "
																	small
															>
                                mdi-filter-variant
                              </v-icon>
                            </v-btn>
                          </template>
                          <v-list class="pa-0" dense flat>
                            <v-list-item-group
																v-model="activeFilters[header.value]"
																class="py-2"
																multiple
														>
                              <template v-for="item in filters[header.value]">
                                <v-list-item
																		:key="`${item}`"
																		:ripple="false"
																		:value="item"
																>
                                  <template v-slot:default="{ active }">
                                    <v-list-item-action>
                                      <v-checkbox
																					color="primary"
																					:input-value="active"
																					:ripple="false"
																					:true-value="item"
																			></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                      <v-list-item-title
																					v-text="item"
																			></v-list-item-title>
                                    </v-list-item-content>
                                  </template>
                                </v-list-item>
                              </template>
                            </v-list-item-group>
                            <v-divider></v-divider>
                            <v-row no-gutters>
                              <v-col>
                                <v-list-item :ripple="false">
                                  <v-list-item-action>
                                    <v-simple-checkbox
																				color="primary"
																				dense
																				:indeterminate="isIndeterminate(header.value)"
																				:ripple="false"
																				:value="
                                        filters[header.value].length ===
                                        activeFilters[header.value].length
                                      "
																				@input="selectAll(header.value)"
																		></v-simple-checkbox>
                                  </v-list-item-action>
                                  <v-list-item-content>
                                    <v-list-item-title>Select All</v-list-item-title>
                                  </v-list-item-content>
                                </v-list-item>
                              </v-col>
                            </v-row>
                          </v-list>
                        </v-menu>
                      </div>
                    </span>
									</template>

									<!-- All special column body/footer slots here -->
									<template
											v-for="(_, scopedSlotName) in $scopedSlots"
											#[scopedSlotName]="slotData"
									>
										<slot v-bind="slotData" :name="scopedSlotName"/>
									</template>
									<template v-for="(_, slotName) in $slots" #[slotName]>
										<slot :name="slotName"/>
									</template>
								</v-data-table>
							</v-col>
						</v-row>
					</ui-box>
				</v-col>
			</v-row>
		</v-col>
	</v-row>
</template>

<script>
// import { inviteHelpers } from '../../../Shared/helpers';

export default {
	name: 'BwTable',
	props: {
		columnsData: {
			type: Array,
			default: () => []
		},
		customSearch: {
			type: Function
		},
		displayFilters: {
			type: Object,
			default: () => {
			}
		},
		forceSelectableRow: {
			type: Boolean,
			default: false
		},
		headers: {
			type: Array,
			default: () => []
		},
		hideRowSelect: {
			type: Boolean
		},
		hideReset: {
			type: Boolean,
			default: false
		},
		hideResetRow: {
			type: Boolean
		},
		hideSearch: {
			type: Boolean
		},
		disableSort: {
			type: Boolean,
			default: false
		},
		items: {
			type: Array,
			default: () => []
		},
		noBorder: {
			type: Boolean
		},
		rowSelect: {
			type: Boolean,
			default: false,
		},
		search: String,
		showColumnSelect: {
			type: Boolean
		},
		showDateFilter: {
			type: String,
			default: ''
		},
		sortBy: {
			type: Array
		},
		sortDesc: {
			type: Boolean,
			default: false
		},
		value: {
			type: Array,
			default: () => []
		}
		
	},
	data() {
		return {
			mq: { ...this.$vuetify.breakpoint },
			activeFilters: {},
			activeHeaders: [],
			columns: this.columnsData,
			dateModal: false,
			filterDateRange: null,
			filteredItems: this.items,
			filters: this.headers.reduce(
					(acc, header) => (header?.filterOn ? { ...acc, [header.filterOn]: [] } : acc),
					{}
			),
			internalSearch: '',
			receivedFilters: {},
			currentFilters: {},
		};
	},
	computed: {
		hasHeaderFilters() {
			return this.displayFilters && Object.keys(this.displayFilters).length > 0
		},
		localAttrs() {
			return {
				// Define any attrs/props that should not be overwritten by the incoming component
				// None at this time...
			};
		},
		maxShowSelectedColumns() {
			if(this.$vuetify.breakpoint.lgAndUp) return 4;
			if(this.$vuetify.breakpoint.mdAndUp) return 3;
			return 2;
		},
		selectedRows: {
			get() {
				return this.value;
			},
			set(value) {
				this.$emit('input', value);
			}
		}
	},
	created() {
		this.rowSelect = this.forceSelectableRow;
		this.initializeFilters();
		this.initializeHeaders();
		this.receivedFilters = this.defaultFilters({});
		this.updateFilteredItems(this.receivedFilters);
		if(this.search !== undefined) {
			this.internalSearch = this.search;
		}
	},
	methods: {
		filterItemsByDate() {
			const { items, moment, showDateFilter: dateField } = this;
			if(!this.filterDateRange) return;
			const [startDate = null, endDate = null] = this.filterDateRange;
			this.filteredItems = items.filter(item => {
				if(!startDate) return true;
				if(!endDate) return moment(item[dateField]).isSameOrAfter(moment(startDate));
				const startOfStartDate = moment(startDate).startOf('day');
				const endOfEndDate = moment(endDate).endOf('day');
				return moment(item[this.showDateFilter]).isBetween(
						startOfStartDate,
						endOfEndDate,
						undefined,
						'[]'
				);
			});
		},
		initializeFilters() {
			for (const col in this.filters) {
				this.filters[col] = this.items
						.map(d => d[col])
						.filter((value, index, self) => self.indexOf(value) === index);
			}
			this.activeFilters = { ...this.filters };
		},

		initializeHeaders() {
			this.activeHeaders = this.headers
					.map(header =>
							header?.filterOn
									? {
										...header,
										filter: value =>
												this.activeFilters[header.filterOn]
														? this.activeFilters[header.filterOn].includes(value)
														: true
									}
									: header
					)
					.filter(header => header?.selected || header.mandatory);
		},

		isIndeterminate(col) {
			const { activeFilters, filters } = this;
			if(activeFilters[col].length === filters[col].length || activeFilters[col].length)
				return null;
			return activeFilters[col].length === filters[col].length;
		},

		selectAll(col) {
			// A programmatic call to toggle select all will trigger this event. Currently it's
			// only being called programmatically to deselect all and therefore we should reset
			// the selected rows.
			if(!col) return this.selectedRows = [];

			// Normal functionality here
			if(this.filters[col].length === this.activeFilters[col].length)
				return this.clearAll(col);
			return this.toggleAll(col);
		},

		toggleAll(col) {
			this.activeFilters[col] = this.items
					.map(d => d[col])
					.filter((value, index, self) => self.indexOf(value) === index);
		},

		clearAll(col) {
			this.activeFilters[col] = [];
		},

		resetTable() {
			this.search = '';
			this.filterDateRange = null;
			this.receivedFilters = this.defaultFilters({});
			this.filterItemsByDate();
			this.initializeFilters();
			this.initializeHeaders();
			this.updateFilteredItems(this.receivedFilters);
		},


		resetFilters() {
			this.resetTable();
		},
		defaultFilters(receivedFilters) {
			return this.displayFilters ? Object.keys(this.displayFilters).reduce((acc, key) => {
				if(receivedFilters[key] !== undefined) {
					acc[key] = receivedFilters[key];
				}
				else if(this.displayFilters[key].default !== undefined) {
					acc[key] = this.displayFilters[key].default;
				}
				return acc;
			}, {}) : {};
		},
		updateFilteredItems(receivedFilters) {
			this.currentFilters = receivedFilters;
			const filtersWithDefaults = this.defaultFilters(receivedFilters);
			const filteredItems = this.items.filter(item => {
				for (const key in filtersWithDefaults) {
					if(this.displayFilters[key].filterOnFalse) {
						if(filtersWithDefaults[key] === false) {
							if(!!item[this.displayFilters[key].filterOnFalse]) {
								return false;
							}
						}
						continue;
					}
					if(!filtersWithDefaults[key] || (Array.isArray(filtersWithDefaults[key]) && !filtersWithDefaults[key].length)) continue;
					const filterOn = this.displayFilters[key].filterOn;
					const itemValue = this.displayFilters[key].filterEmpty &&
					(!item[filterOn] || (Array.isArray(item[filterOn]) && item[filterOn].length == 0))
							? -1
							: item[filterOn];
					if(Array.isArray(filtersWithDefaults[key]) && filtersWithDefaults[key].length > 0) {
						if(Array.isArray(itemValue)) {
							if(!itemValue.some(r => filtersWithDefaults[key].includes(r))) {
								return false;
							}
						}
						else {
							if(!filtersWithDefaults[key].includes(itemValue)) {
								return false;
							}
						}
					}
					else {
						if(Array.isArray(itemValue)) {
							if(!itemValue.includes(filtersWithDefaults[key])) {
								return false;
							}
						}
						else {
							if(filtersWithDefaults[key] !== itemValue) {
								return false;
							}
						}
					}
				}
				return true;
			});
			this.filteredItems = filteredItems;
		},
	},
	watch: {
		items: {
			deep: true,
			handler() {
				this.updateFilteredItems(this.currentFilters)
			},
		},
		search(newValue) {
			this.internalSearch = newValue;
		}
	}
};
</script>

<style lang="scss" scoped>
@use 'sass:color';
@import '~vuetify/src/styles/styles.sass';
@import '~vuetify/src/styles/settings/_variables';

.v-data-table {
	background: none !important;
	border-radius: map-get($rounded, 'lg');

	& ::v-deep .v-data-table__wrapper > table {
		& > thead {
			background: none !important;

			& tr > th {
				background: none !important;
				// background-color: map-get($grey, 'lighten-5');
				border: none !important;
				position: relative;
				white-space: nowrap;

				&:first-child {
					border-top-left-radius: map-get($rounded, 'lg');
				}

				&:last-child {
					border-top-right-radius: map-get($rounded, 'lg');
				}
			}
		}

		& > tbody {
			border-radius: map-get($rounded, 'lg');

			& > tr {
				border-radius: map-get($rounded, 'lg');

				& > td:nth-child(2) {
					& > div.v-data-table__mobile-row__cell {
						flex-grow: 1;
						max-width: 50%; // testing this...
					}
				}

				& > td {
					background: white;
					height: 81px !important;
					min-height: 81px !important;
					padding-bottom: 1rem !important;
					padding-top: 1rem !important;
					position: relative;

					@media #{map-get($display-breakpoints, 'xs-only')} {
						height: inherit !important;
						min-height: inherit !important;
						padding-bottom: 0.5rem !important;
						padding-top: 0.5rem !important;

						&:not(:first-child)::after {
							background-color: map-get($grey, 'lighten-3');
							content: '';
							height: 1px;
							left: 0;
							position: absolute;
							top: 0;
							width: 100%;
						}
					}
				}

				&:first-child {
					& > td {
						border-top: solid 1px #d1f1c1;
					}

					&.v-data-table__selected > td {
						border-top: solid 1px #b6e99c;
					}

					& > td:first-child {
						border-top-left-radius: map-get($rounded, 'lg');
					}

					& > td:last-child {
						border-top-right-radius: map-get($rounded, 'lg');
					}
				}

				&:last-child {
					& > td {
						border-bottom: solid 1px #d1f1c1;
					}

					&.v-data-table__selected > td {
						border-bottom: solid 1px #b6e99c;
					}

					& > td:first-child {
						border-bottom-left-radius: map-get($rounded, 'lg');
					}

					& > td:last-child {
						border-bottom-right-radius: map-get($rounded, 'lg');
					}
				}

				&:hover {
					background-color: saturate(mix(white, #7a9176, 91%), 30%) !important;

					& > td {
						background-color: saturate(mix(white, #7a9176, 91%), 30%);
					}
				}

				&.v-data-table__selected {
					background-color: saturate(mix(white, #7a9176, 85%), 30%) !important;

					& > td {
						background-color: saturate(mix(white, #7a9176, 85%), 30%);
					}
				}
			}
		}
	}

	&.v-data-table--dense {
		& ::v-deep .v-data-table__wrapper > table {
			& td {
				height: 32px !important;
			}
		}
	}

	& > ::v-deep .v-data-footer {
		background: none !important;
		// background-color: map-get($grey, 'lighten-5');
		border: none !important;
		border-radius: 0 0 map-get($rounded, 'lg') map-get($rounded, 'lg');
	}

	& ::v-deep .v-input--selection-controls__input {
		margin-right: 0 !important;
	}
}

.v-select--is-multi .v-select__selections {
	flex-wrap: nowrap !important;
}

.filter-menu-btn {
	position: absolute;
	right: 0;
	top: 5px;
}

#main-content:not(.with-main-bg) {
	.v-data-table {

		& ::v-deep table {

			& > tbody {

				& > tr {

					&:first-child {
						& > td {
							border-top: solid 1px #f0f0f0;
						}

						&.v-data-table__selected > td {
							border-top: solid 1px #b6e99c;
						}

						& > td:first-child {
							border-top-left-radius: map-get($rounded, 'lg');
						}

						& > td:last-child {
							border-top-right-radius: map-get($rounded, 'lg');
						}
					}

					&:last-child {
						& > td {
							border-bottom: solid 1px #e5e5e5;
						}

						&.v-data-table__selected > td {
							border-bottom: solid 1px #b6e99c;
						}

						& > td:first-child {
							border-bottom-left-radius: map-get($rounded, 'lg');
						}

						& > td:last-child {
							border-bottom-right-radius: map-get($rounded, 'lg');
						}
					}

					&:hover {
						& > td {
							background-color: saturate(mix(white, #7a9176, 91%), 30%);
						}
					}

					&.v-data-table__selected {
						& > td {
							background-color: saturate(mix(white, #7a9176, 85%), 30%);
						}
					}
				}
			}

			& .v-data-table__expanded__content td {
				border-radius: 0 !important;
			}

			& tr.v-data-table__expanded__content {
				border-radius: 0 !important;
				box-shadow: inset 0 3px 5px -1px rgba(0, 0, 0, 0.13), inset 0 6px 10px 0 rgba(0, 0, 0, 0.1), inset 0 1px 18px 0 rgba(0, 0, 0, 0.08) !important;
			}
		}
	}
}

::v-deep {
	& .mark-complete,
	& .mark-complete:hover {
		background: rgba(76, 175, 80, 0.07) !important;
	}

	& .mark-incomplete,
	& .mark-incomplete:hover {
		background: #fff7ed !important;
	}

	& .mark-disabled,
	& .mark-disabled:hover {
		background: #fafafa !important;
	}

	& td,
	&:hover td {
		background: none !important;
	}
}

</style>
